/* eslint-disable max-len */
import React from 'react'
import { InferType } from 'yup'
import { captureException } from '@sentry/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Modal } from '../../../../common/Modal'
import { ModalHeader } from '../../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../../common/Modal/ModalBody'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { ToastType } from '../../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../../hooks/useToastNotification'
import { societyDocumentFolderCreateSchema } from '../../../../../forms/schemas/society_document_folder_create'
import { CreateUpdateSocietyWebsiteFolderForm } from '../CreateUpdateSocietyWebsiteFolderForm'
import {
  ISocietyWebsiteModel,
  IWebsiteFolder,
} from '../../../../../interfaces/models/societies-websites.interfaces'
import {
  patchWebsite,
  websiteQueries,
} from '../../../../../api/society-websites'

interface CreateUpdateSocietyWebsiteFolderModalProps {
  website: ISocietyWebsiteModel
  show: boolean
  close: () => void
  children?: React.ReactNode
  id?: string
}

export const CreateUpdateSocietyWebsiteFolderModal = ({
  website,
  id,
  children,
  show,
  close,
}: CreateUpdateSocietyWebsiteFolderModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()

  const updateMode = !!id
  const folders = website.foldersList
  const folder = id ? folders?.find((_folder) => _folder._id === id) : undefined

  const showErrorMessage = (): void => {
    const message = updateMode
      ? 'createUpdateFolder.flashMessage.updateFolderFailure'
      : 'createUpdateFolder.flashMessage.createFolderFailure'
    setToastNotification(ToastType.DANGER, translate(message))
  }

  const showSuccessMessage = (): void => {
    const message = updateMode
      ? 'createUpdateFolder.flashMessage.updateFolderSuccess'
      : 'createUpdateFolder.flashMessage.createFolderSuccess'
    setToastNotification(ToastType.SUCCESS, translate(message))
  }

  const queryClient = useQueryClient()

  const { mutate: mutatePatchWebsite, isPending: isPatchingWebsite } =
    useMutation({
      mutationFn: patchWebsite,
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: websiteQueries.getWebsitePerSociety(website.societyId)
            .queryKey,
        })
        close()
        showSuccessMessage()
      },
      onError: () => {
        showErrorMessage()
      },
    })

  const onSubmit = async (
    data: InferType<typeof societyDocumentFolderCreateSchema>
  ): Promise<void> => {
    if (updateMode) {
      mutatePatchWebsite({
        id: website._id,
        body: {
          foldersList: folders?.map((_folder) => {
            if (_folder._id === id) {
              return { ..._folder, name: data.name }
            }
            return _folder
          }),
        },
      })
    } else {
      const foo = (folders || []) satisfies IWebsiteFolder[]
      const documentsList = [] satisfies IWebsiteFolder['documentsList']
      mutatePatchWebsite({
        id: website._id,
        body: {
          foldersList: [
            ...foo,
            { name: data.name, documentsList },
          ] as IWebsiteFolder[],
        },
      })
    }
  }

  const onError = (error: unknown): void => {
    captureException(error)
    showErrorMessage()
  }

  const loading = isPatchingWebsite

  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: (
            <ModalHeader onClose={close}>
              {translate(
                updateMode
                  ? 'createUpdateFolder.editFolderTitle'
                  : 'createUpdateFolder.addNewFolderTitle'
              )}
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              <CreateUpdateSocietyWebsiteFolderForm
                onError={onError}
                onSubmit={onSubmit}
                onClose={close}
                loading={loading}
                folder={folder}
              />
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
